import { AlertBox } from '@components/ui';
import type { Issue, UserInsights } from '@hooks/useInsights';
import { BarChart3, PlusCircle, X } from 'lucide-react';
import type { ReactElement } from 'react';
import { AIAnalysisSummary } from './AIAnalysisSummary';

interface ParlayLevelAnalysisProps {
  insights: UserInsights | null;
  issues: Issue[];
  dismissIssue: (issueId: number) => Promise<{ success: boolean }>;
}

export function ParlayLevelAnalysis({
  insights,
  issues,
  dismissIssue,
}: ParlayLevelAnalysisProps): ReactElement {
  const hasData = insights && insights.totalBets > 0;

  if (!hasData) {
    return (
      <div>
        <h2 className="text-lg font-bold mb-3 text-white">Performance Analysis (Parlay Level)</h2>
        <AlertBox variant="gray" className="mb-4">
          <div className="text-center py-8">
            <BarChart3
              className="w-16 h-16 text-theme-text-tertiary mx-auto mb-4"
              strokeWidth={1.5}
            />
            <p className="text-theme-text-secondary text-lg mb-2">No betting data yet</p>
            <p className="text-theme-text-tertiary text-sm">
              Start tracking your bets to see personalized analysis and insights.
            </p>
          </div>
        </AlertBox>
      </div>
    );
  }

  // Group issues by type
  const criticalIssues = issues.filter((i) => i.issueType === 'critical');
  const warningIssues = issues.filter((i) => i.issueType === 'warning');
  const infoIssues = issues.filter((i) => i.issueType === 'info');

  // Extract parlay size performance
  const parlaySizePerf = insights.parlaySizePerformance || {};
  const smallParlays = parlaySizePerf['2'] || parlaySizePerf['3'] || { wins: 0, losses: 0 };

  return (
    <div>
      <h2 className="text-lg font-bold mb-3 text-white">
        Your Performance Analysis (Parlay Level)
      </h2>
      <AlertBox variant="gray" className="mb-6">
        <p className="text-sm text-theme-text-secondary">
          <strong className="text-theme-warning">Note:</strong> This analysis is at the{' '}
          <em>parlay level</em>, not individual legs. A parlay loss doesn't mean every leg lost -
          one failed leg kills the whole bet. Toggle to "Leg Level" for individual prop analysis.
        </p>
      </AlertBox>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 @3xl:grid-cols-4 gap-4 mb-4">
        <div className="bg-theme-surface rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-theme-primary">{insights.totalBets}</div>
          <div className="text-theme-text-tertiary text-sm">Total Bets</div>
        </div>
        <div className="bg-theme-surface rounded-lg p-4 text-center">
          <div
            className={`text-2xl font-bold ${parseFloat(insights.winRate) >= 45 ? 'text-theme-success' : 'text-theme-danger'}`}
          >
            {insights.winRate}%
          </div>
          <div className="text-theme-text-tertiary text-sm">Win Rate</div>
          <div className="text-theme-text-tertiary text-xs">
            {insights.totalWins}W - {insights.totalLosses}L
          </div>
        </div>
        <div className="bg-theme-surface rounded-lg p-4 text-center">
          <div
            className={`text-2xl font-bold ${parseFloat(insights.roi) >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
          >
            {parseFloat(insights.roi) >= 0 ? '+' : ''}
            {insights.roi}%
          </div>
          <div className="text-theme-text-tertiary text-sm">ROI</div>
        </div>
        <div className="bg-theme-surface rounded-lg p-4 text-center">
          <div
            className={`text-2xl font-bold ${insights.currentStreak >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
          >
            {insights.currentStreak >= 0
              ? `${insights.currentStreak}W`
              : `${Math.abs(insights.currentStreak)}L`}
          </div>
          <div className="text-theme-text-tertiary text-sm">Current Streak</div>
        </div>
      </div>

      {/* AI Analysis Summary (when available) */}
      {insights.aiAnalysis && <AIAnalysisSummary aiAnalysis={insights.aiAnalysis} />}

      <div className="space-y-4">
        {/* Critical Issues */}
        {criticalIssues.length > 0 && (
          <AlertBox variant="red" title="Critical Issues">
            <ul className="space-y-3 text-sm">
              {criticalIssues.map((issue) => (
                <li key={issue.id} className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <strong>{issue.title}:</strong> {issue.description}
                    {issue.recommendation && (
                      <span className="text-theme-text-tertiary ml-1">
                        ({issue.recommendation})
                      </span>
                    )}
                    {issue.aiExplanation && (
                      <div className="mt-2 p-2 bg-theme-surface/50 rounded border-l-2 border-purple-500">
                        <div className="flex items-center gap-1 text-xs text-purple-400 mb-1">
                          <PlusCircle className="w-3 h-3" />
                          AI Analysis
                        </div>
                        <p className="text-theme-text-secondary text-xs">{issue.aiExplanation}</p>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => dismissIssue(issue.id)}
                    className="text-theme-text-tertiary hover:text-theme-text-secondary flex-shrink-0"
                    title="Dismiss"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </AlertBox>
        )}

        {/* Warnings */}
        {warningIssues.length > 0 && (
          <AlertBox variant="yellow" title="Patterns to Watch">
            <ul className="space-y-3 text-sm">
              {warningIssues.map((issue) => (
                <li key={issue.id} className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <strong>{issue.title}:</strong> {issue.description}
                    {issue.aiExplanation && (
                      <div className="mt-2 p-2 bg-theme-surface/50 rounded border-l-2 border-purple-500">
                        <div className="flex items-center gap-1 text-xs text-purple-400 mb-1">
                          <PlusCircle className="w-3 h-3" />
                          AI Analysis
                        </div>
                        <p className="text-theme-text-secondary text-xs">{issue.aiExplanation}</p>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => dismissIssue(issue.id)}
                    className="text-theme-text-tertiary hover:text-theme-text-secondary flex-shrink-0"
                    title="Dismiss"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </AlertBox>
        )}

        {/* What's Working */}
        {(insights.biggestWin ||
          parseFloat(insights.winRate) >= 50 ||
          smallParlays.wins > smallParlays.losses) && (
          <AlertBox variant="green" title="What's Working">
            <ul className="space-y-2 text-sm">
              {parseFloat(insights.winRate) >= 50 && (
                <li>
                  <strong>Above average win rate:</strong> {insights.winRate}% is solid. Stay
                  disciplined.
                </li>
              )}
              {smallParlays.wins > 0 && smallParlays.wins >= smallParlays.losses && (
                <li>
                  <strong>2-3 leg parlays:</strong> {smallParlays.wins}-{smallParlays.losses}{' '}
                  record. Keep it simple!
                </li>
              )}
              {insights.biggestWin && (
                <li>
                  <strong>Best win:</strong> +$
                  {parseFloat(String(insights.biggestWin.amount)).toFixed(0)} profit
                  {insights.biggestWin.odds && ` at ${insights.biggestWin.odds}`}.
                </li>
              )}
              {insights.longestWinStreak && insights.longestWinStreak > 2 && (
                <li>
                  <strong>Best streak:</strong> {insights.longestWinStreak} wins in a row.
                </li>
              )}
            </ul>
          </AlertBox>
        )}

        {/* Info messages */}
        {infoIssues.length > 0 && (
          <AlertBox variant="blue" title="Info">
            <ul className="space-y-2 text-sm">
              {infoIssues.map((issue) => (
                <li key={issue.id}>
                  <strong>{issue.title}:</strong> {issue.description}
                </li>
              ))}
            </ul>
          </AlertBox>
        )}

        {/* Getting started message for low bet counts */}
        {insights.totalBets > 0 && insights.totalBets < 10 && issues.length === 0 && (
          <AlertBox variant="blue" title="Getting Started">
            <p className="text-sm text-theme-text-secondary">
              You have {insights.totalBets} bet
              {insights.totalBets !== 1 ? 's' : ''} tracked. Keep logging your bets to unlock
              personalized insights and identify patterns in your betting.
            </p>
          </AlertBox>
        )}
      </div>
    </div>
  );
}
